import type { DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers';
import { type JSX, useCallback } from 'react';
import { type FieldPathByValue, type FieldValues, useController } from 'react-hook-form';
import { DatePickerField, type DatePickerFieldProps } from './DatePickerField.tsx';

type RHFDatePickerFieldProps<T extends FieldValues> = Omit<DatePickerFieldProps, 'name' | 'value'> & {
  /**
   * The value of the date picker field should be a `Date` object or `null` if no date is selected.
   *
   * @see {@link https://mui.com/x/migration/migration-pickers-v5/#update-the-format-of-the-value-prop|`value` prop format}
   */
  name: FieldPathByValue<T, Date | null>;
};

export const RHFDatePickerField = <T extends FieldValues>({
  name,
  onChange,
  ...rest
}: RHFDatePickerFieldProps<T>): JSX.Element => {
  const {
    field: { onChange: onRHFChange, value, ...fieldProps },
    fieldState: { error, invalid },
    formState: { isSubmitting },
  } = useController({ name });

  const handleChange = useCallback(
    (value: Date | null, context: PickerChangeHandlerContext<DateValidationError>) => {
      onChange ? onChange(value, context) : onRHFChange(value);
    },
    [onChange, onRHFChange]
  );

  const helperText = error?.message || rest.helperText;
  const isDisabled = isSubmitting || rest.disabled;
  const isError = invalid || !!rest.error;

  return (
    <DatePickerField
      {...fieldProps}
      {...rest}
      disabled={isDisabled}
      error={isError}
      helperText={helperText}
      onChange={handleChange}
      value={value}
    />
  );
};
